import React, { PropTypes } from 'react';
import { red600, orange500 } from 'material-ui/styles/colors';
import AlertWarning from 'material-ui/svg-icons/alert/warning';
import jquery from 'jquery';
import moment from 'moment';
import { values, flatMap, groupBy, mapValues, sortBy } from 'lodash';
import config from 'config'; // eslint-disable-line
import radium from 'radium';

import { getRegionStats } from '../../utils';
import css from './Map.css';
import FullscreenButton from './FullscreenButton';
import MyLocationButton from './MyLocationButton';
import { styles, icons } from './styles';

const focusZoom = 9;
const markerThreshold = 1;
const dateTimeFormat = 'D.M.Y HH:mm';

const isGeolocationAvailable = () => 'geolocation' in navigator;

const getInfoContent = (station) => {
  const isFault = station.outage.type.toUpperCase() === 'FAULT';
  const heading = isFault ? 'Vikakeskeytys' : 'Työkeskeytys';

  const starts = station.startedAt || station.outage.startedAt || station.outage.estimatedStart;
  const startsValue = starts ? moment(starts).format(dateTimeFormat) : 'Ei tiedossa';
  const startsLabel = (station.startedAt || station.outage.startedAt) ?
    'Alkamisaika' : 'Arvioitu alkamisaika';

  const ends = station.endedAt || station.outage.estimatedEnd;
  const endsValue = ends ? moment(ends).format(dateTimeFormat) : 'Ei tiedossa';
  const endsLabel = station.endedAt ? 'Päättymisaika' : 'Arvioitu päättymisaika';

  const iconColor = isFault ? red600 : orange500;
  const icon = `<svg viewBox="0 0 24 24" style="display: inline-block; fill: ${iconColor}; height: 24px; width: 24px; transition: all 450ms cubic-bezier(0.23, 1, 0.32, 1) 0ms; -webkit-user-select: none;"><path d="M1 21h22L12 2 1 21zm12-3h-2v-2h2v2zm0-4h-2v-4h2v4z"></path></svg>`; // eslint-disable-line
  return `
  <div class="map-info-window">
    <div class="heading">
      ${icon}
      <h4>${heading}</span>
    </div>
    <p>Sähköttömät asiakkaat: ${station.customers}</p>
    <p>${startsLabel}: ${startsValue}</p>
    <p>${endsLabel}: ${endsValue}</p>
  </div>`;
};

const getRegionInfoContent = (region) => {
  const stats = getRegionStats(region);
  const faultsMsg = stats.faults.count ?
    `${stats.faults.count} (${stats.faults.customers} asiakasta)` : '0';
  const maintenancesMsg = stats.maintenances.count ?
    `${stats.maintenances.count} (${stats.maintenances.customers} asiakasta)` : '0';
  return `
  <div class="map-info-window">
    <div class="heading">
      <h4>${region.name}</span>
    </div>
    <p>Vikakeskeytykset: ${faultsMsg}</p>
    <p>Työkeskeytykset: ${maintenancesMsg}</p>
    <p>Sähköttömät asiakkaat: ${stats.faults.customers + stats.maintenances.customers}</p>
  </div>`;
};

const kml = {
  url: `${config.kml.url}?time=${Date.now()}`,
  options: {
    suppressInfoWindows: true,
    preserveViewport: false,
  },
};

class Map extends React.Component {
  static propTypes = {
    regions: PropTypes.object.isRequired,
    selectedRegion: PropTypes.object,
    timeSpan: PropTypes.string.isRequired,
  }

  constructor(props) {
    super(props);
    this.markers = [];
  }

  state = {
    fullscreen: false,
    location: null,
    locating: false,
    infoWindowRegionName: null,
  };

  componentWillMount() {
    document.addEventListener('keydown', this.handleKeyDown);
  }

  componentWillReceiveProps(nextProps) {
    if (nextProps.selectedRegion !== this.props.selectedRegion) {
      const target = jquery('#map').offset().top - 15;
      const distance = jquery(window).scrollTop() - target;
      const time = Math.pow(distance, 0.75) * 1.2;
      jquery('body').animate({ scrollTop: target }, time, 'swing', () => {
        this.fitBoundsForRegion(nextProps.selectedRegion);
      });
    }

    if (nextProps.regions !== this.props.regions) {
      this.updateRegionMarkers(nextProps.regions);
    }

    if (nextProps.timeSpan !== this.props.timeSpan) {
      this.setKmlLayer(`${kml.url}&timeSpan=${nextProps.timeSpan}`);
    }

    if (this.state.infoWindowRegionName) {
      const region = nextProps.regions[this.state.infoWindowRegionName];
      this.infoWindow.setContent(getRegionInfoContent(region));
    }
  }

  componentWillUnmount() {
    document.removeEventListener('keydown', this.handleKeyDown);

    if (isGeolocationAvailable()) {
      navigator.geolocation.clearWatch(this.locationWatcher);
    }
  }

  onMapMount = (ref) => {
    const map = new window.google.maps.Map(ref, {
      center: new window.google.maps.LatLng(67.415768, 26.588458),
      zoom: 6,
      options: {
        mapTypeControl: false,
      },
    });
    this.map = map;

    // Add controls
    const input = document.getElementById('pac-input');
    const searchBox = new window.google.maps.places.SearchBox(input);
    map.controls[window.google.maps.ControlPosition.TOP_LEFT].push(input);
    this.searchBox = searchBox;

    searchBox.addListener('places_changed', () => {
      const places = searchBox.getPlaces();
      const position = places[0].geometry.location;
      this.map.setZoom(focusZoom);
      this.map.panTo(position);
      if (this.searchMarker) {
        this.searchMarker.setMap(null);
      }
      this.searchMarker = new window.google.maps.Marker({
        position,
        map: this.map,
      });
    });

    // Bias the SearchBox results towards current map's viewport.
    map.addListener('bounds_changed', () => {
      searchBox.setBounds(map.getBounds());
    });

    // Show markers only when zoomed in
    map.addListener('zoom_changed', () => {
      this.updateRegionMarkers(this.props.regions);
    });

    // Add info window for outages
    this.infoWindow = new window.google.maps.InfoWindow();

    // Draw regions
    this.setKmlLayer(kml.url);
  };

  setKmlLayer = (url) => {
    if (this.kmlLayer) {
      this.kmlLayer.setMap(null);
    }
    this.kmlLayer = new window.google.maps.KmlLayer(url, { ...kml.options, map: this.map });
    window.google.maps.event.addListener(this.kmlLayer, 'click', (e) => {
      const region = this.props.regions[e.featureData.name];
      this.infoWindow.setContent(getRegionInfoContent(region));
      this.infoWindow.open(this.map);
      this.infoWindow.setPosition(e.latLng);
      this.fitBoundsForRegion(region);
      this.setState({ infoWindowRegionName: e.featureData.name });
    });
  };

  getMapStyle = () => (this.state.fullscreen ? styles.mapFull : styles.map);

  setPositionMarker = (position) => {
    if (this.positionMarker) {
      this.positionMarker.setMap(null);
    }
    this.positionMarker = new window.google.maps.Marker({
      position,
      map: this.map,
      icon: icons.location,
    });
  };

  handleMyLocationClick = () => {
    if (!this.state.location) {
      this.setState({ locating: true });
      this.locationWatcher = window.navigator.geolocation.watchPosition((position) => {
        const { coords: { latitude, longitude } } = position;
        const location = { lat: latitude, lng: longitude };
        this.setPositionMarker(location);

        if (!this.state.location) {
          this.setState({ location, locating: false });
          this.map.setZoom(focusZoom);
          this.map.panTo(location);
        } else {
          this.setState({ location });
        }
      });
    } else {
      this.map.setZoom(focusZoom);
      this.map.panTo(this.state.location);
    }
  };


  handlePlacesChanged = () => {
    const places = this.searchBox.getPlaces();
    const location = places[0].geometry.location;

    this.map.setZoom(focusZoom);
    this.map.panTo(location);
  }

  handleKeyDown = (e) => {
    const KEY_ESC = 27;
    if (e.keyCode === KEY_ESC) {
      this.setState({ fullscreen: false }, () => {
        window.google.maps.event.trigger(this.map, 'resize');
      });
    }
  }

  handleFullscreenClick = () => {
    this.setState({
      fullscreen: !this.state.fullscreen,
    }, () => {
      window.google.maps.event.trigger(this.map, 'resize');
    });
  }

  updateRegionMarkers = (regions) => {
    this.markers.forEach((marker) => marker.setMap(null));
    this.markers = [];

    if (this.map.getZoom() >= markerThreshold) {
      // Get all outages and group by station code (one station might have multiple outages)
      const stations = flatMap(regions, (region) => region.stations);
      const groupedStations = groupBy(stations, 'stationCode');
      const sortedGroupedStations = mapValues(groupedStations,
        (outages) => sortBy(outages, (outage) => outage.startedAt || outage.estimatedStart));

      values(sortedGroupedStations).forEach((stationOutages) => {
        const info = stationOutages.reduce((html, outage) => html + getInfoContent(outage), '');
        const fault = stationOutages.find((station) => station.outage.type === 'fault');
        const { lat, lng } = stationOutages[0];
        const marker = new window.google.maps.Marker({
          position: { lat, lng },
          map: this.map,
          icon: fault ? icons.fault : icons.maintenance,
          clickable: true,
          info,
        });
        this.markers.push(marker);
        window.google.maps.event.addListener(marker, 'click', () => {
          this.infoWindow.setContent(marker.info);
          this.infoWindow.open(this.map, marker);
        });
      });
    }
  };

  fitBoundsForRegion = (region) => {
    if (region) {
      const bounds = new window.google.maps.LatLngBounds();
      region.polygon.forEach((coords) => {
        const point = new window.google.maps.LatLng({ lat: coords[1], lng: coords[0] });
        bounds.extend(point);
      });
      this.map.fitBounds(bounds);
    } else {
      this.map.fitBounds(this.kmlLayer.getDefaultViewport());
    }
  };

  render() {
    return (
      <div style={styles.root}>
        <input id="pac-input" type="text" placeholder="Etsi osoittella" style={styles.search} />
        <div id="map" style={this.getMapStyle()} ref={this.onMapMount} />
        <div style={this.state.fullscreen ? styles.controlsFull : {}} className={css.controls}>
          <FullscreenButton
            style={styles.fullscreenIcon}
            className={css.icon}
            fullscreen={this.state.fullscreen}
            onClick={this.handleFullscreenClick}
          />
          {isGeolocationAvailable() ?
            <MyLocationButton
              style={styles.myLocationIcon}
              className={css.icon}
              onClick={this.handleMyLocationClick}
              searching={this.state.locating}
            /> :
            null
          }
        </div>
        <div className={css.mapFooter}>
          <div className={css.legend}>
            <ul style={styles.legend}>
              <li style={styles.legendItem}>
                <AlertWarning style={{ fill: red600 }} />
                <span style={styles.legendLabel}>Vikakeskeytys</span>
              </li>
              <li style={styles.legendItem}>
                <AlertWarning style={{ fill: orange500 }} />
                <span style={styles.legendLabel}>Työkeskeytys</span>
              </li>
            </ul>
          </div>
        </div>
      </div>
    );
  }
}

export default radium(Map);



// WEBPACK FOOTER //
// ./src/components/Map/Map.js